import {
  Dialog as KendoDialog,
  DialogActionsBar,
  DialogCloseEvent
} from '@progress/kendo-react-dialogs';
import { Keys } from '@progress/kendo-react-common';
import React, { ReactPortal } from 'react';
import ReactDOM from 'react-dom';
import { DialogPosition } from '../../Types';
import {
  ActionType,
  ChildrenType,
  Component,
  CustomTagType,
  DialogButton,
  DialogConfig,
  Dispatcher,
  DockType,
  MAIN_DISPATCHER_ID,
  OperationType,
  Position,
  UpdateDialogScope
} from '../../../index';
import { Button } from '@progress/kendo-react-buttons';
import _ from 'lodash';
import DispatcherWindowManager, {
  ANIMATION_CONTAINER_CLASS
} from '../../../utility/window/WindowManager';
import CustomTag from '../customtag/CustomTag';
import uuid from 'uuid-random';
import CustomButton from '../../../components/uncategorized/CustomButton';
import QuerySelector from '../../../utility/QuerySelector';
import classNames from 'classnames';
import {
  getNodesFromContainerFromTypes,
  removeAt
} from '../../../utility/Util';
import Resizer from '../Resizer';
import { WARNING_DIALOG_STATE, WARNING_DIALOG_PROPS } from './Types';
import { HANDLE_RESIZE_PROPS, POSITION } from '../Types';

class WarningDialog extends Component<WARNING_DIALOG_PROPS, WARNING_DIALOG_STATE> {
  protected baseClassPrefix: string = 'tp-dialog-';
  private defaultSize: string = 'md';
  readonly queryId: string = `#${DispatcherWindowManager.DIALOG_CONTAINER_ID.warning}`;
  protected closeAction: string | null = null;
  protected escapeActionName: string = '';
  private defaultFooterState: any = {};
  protected multiWindow = true;
  private multiWindowParams: any = [];
  private dispatcherId: string = MAIN_DISPATCHER_ID;
  private dialogNodeElements: any[] = [];
  protected enableToSetFocusToFirstField: boolean = true;
  protected lastFocusedElement: any = null;
  /**
   * Kendo's closeIcon wil be moved to title bar to endCorner section
   * @private {Boolean} fakeCloseButton (TODO When Kendo changes version with updated
   * Dialog and DialogTitleBar because it should add one property to Dialog and DialogTitleBar and changing
   * closeIcon property from type boolean to type any in order to user can change Kendo
   * html structure into his).
   * Then it should change value of this property to false
   */
  protected readonly fakeCloseButton: boolean = true;
  protected fakeCloseIconRef: any = null;
  private dragXPos: number = -1;
  private dragYPos: number = -1;
  private dragContainer: any;

  state: WARNING_DIALOG_STATE = {
    windows: [],
    buttons: [],
    closable: true,
    children: null,
    dispatcherId: MAIN_DISPATCHER_ID,
    layerCSS: 'tp-display-none',
    title: 'WARNING!',
    iconCSS: 'tp-warning-icon',
    placeholders: {},
    className: undefined
  };

  warningClassName = {
    info: {
      className: 'sm info'
    },
    error: {
      className: 'sm error'
    },
    success: {
      className: 'sm success'
    },
    warning: {
      className: 'sm warning'
    }
  };

  constructor(props) {
    super(props);
    this.fakeCloseIconRef = React.createRef();
  }

  show(configWarningDialog: DialogConfig): void {
    let {
      accessibility,
      buttons = [],
      closable = true,
      dispatcherId = MAIN_DISPATCHER_ID,
      icon,
      text,
      title,
      subTitle,
      placeholders = {},
      previousFocus,
      className = this.defaultSize,
      warningType,
      skipTranslation = false, // if skipTranslation is false, then title and text are sent from server and we do not localise them
      resizable = false
    } = configWarningDialog;
    this.dispatcherId = dispatcherId;
    let children: ChildrenType[] = null;

    if (Dispatcher.appDispatchers[dispatcherId]) {
      children = Dispatcher.appDispatchers[dispatcherId].getDialogChildrenRenderers();
      if (!children.length) {
        children = null;
      }
    }
    let windows;
    if (this.multiWindow) {
      const windowObj = {
        accessibility,
        buttons,
        className,
        closable,
        children,
        dispatcherId,
        iconCSS: icon,
        layerCSS: 'tp-warning-dialog',
        placeholders,
        title: skipTranslation && title ? title : this.translate(title, placeholders),
        subTitle: skipTranslation && subTitle ? subTitle : this.translate(subTitle, placeholders),
        warningType,
        resizable
      };
      if (text) {
        _.assign(windowObj, {
          text: skipTranslation ? text : this.translate(text, placeholders)
        });
      }
      windows = this.state.windows.slice();
      windows.push(windowObj);
      this.multiWindowParams.push({
        closeAction: null,
        escapeActionName: '',
        escapeActionStopped: false,
        defaultFooterState: {}
      });
      DispatcherWindowManager.openWindow(this.moduleName, {
        previousFocus,
        configWarningDialog
      });
      this.setMultiWindowState(windows);
    } else {
      DispatcherWindowManager.openWindow(this.moduleName, {
        previousFocus,
        configWarningDialog
      });
      this.setState({
        accessibility,
        buttons,
        className,
        closable,
        children,
        dispatcherId,
        iconCSS: icon,
        layerCSS: 'tp-warning-dialog',
        placeholders,
        title: skipTranslation && title ? title : this.translate(title, placeholders),
        subTitle: skipTranslation && subTitle ? subTitle : this.translate(subTitle, placeholders),
        resizable
      });
      if (text) {
        this.setState({
          text: skipTranslation ? text : this.translate(text, placeholders)
        });
      }
    }
  }

  hide(configWarningDialog?: DialogConfig): void {
    this.dispatcherId = _.get(configWarningDialog, 'dispatcherId');
    if (!this.dispatcherId) {
      this.dispatcherId = MAIN_DISPATCHER_ID;
    }
    let windows = [];
    let enableRemove = false;
    let index;
    if (this.multiWindow) {
      let pos = _.get(configWarningDialog, 'pos', DialogPosition.last);
      windows = this.state.windows.slice();
      const len = windows.length;
      if (len) {
        switch (pos) {
          case DialogPosition.last:
            index = len - 1;
            break;
          case DialogPosition.previous:
            index = len > 1 ? len - 2 : 0;
            break;
          default:
            index = pos;
            break;
        }
        if (index === DialogPosition.all) {
          index = -1; // index has value of -1 when all windows are closed
          for (let i = windows.length - 1; i > -1; i--) {
            removeAt(windows, i);
            removeAt(this.multiWindowParams, i);
          }
          enableRemove = true;
        } else if (index === DialogPosition.allPrevious) {
          for (let i = windows.length - 2; i > -1; i--) {
            removeAt(windows, i);
            removeAt(this.multiWindowParams, i);
            index = i;
            if (['Widget', 'DownloadWidget'].indexOf(this.moduleName) === -1) {
              DispatcherWindowManager.closeWindow(this.moduleName, {
                index
              });
            }
          }
          enableRemove = false;
        } else if (index > -1 && index < len) {
          removeAt(windows, index);
          removeAt(this.multiWindowParams, index);
          enableRemove = true;
        } else {
          throw 'Wrong Hiding Dialog/Warning Index!';
        }
      }
      this.setMultiWindowState(windows);
    } else {
      enableRemove = true;
      this.setState({
        ...this.defaultFooterState,
        layerCSS: 'tp-display-none',
        text: null
      });
    }
    if (enableRemove && ['Widget', 'DownloadWidget'].indexOf(this.moduleName) === -1) {
      DispatcherWindowManager.closeWindow(this.moduleName, {
        index
      });
    }
  }

  /**
   * Returns dom element of active dialog
   * @returns {HTMLElement}
   */
  private getActiveDialogNodeDom = (): HTMLElement => {
    let domNodeBody = null;
    const { windows } = this.state;
    const len = windows?.length;
    if (len) {
      domNodeBody = this.getDomNode(_.get(this, `refs.${this.moduleName}_${len - 1}`));
    }
    return domNodeBody;
  };

  /**
   * Sets property 'dialogNodeElements' with focusable nodes of dialog
   * @param {HTMLElement} domNodeBody Dom element of active dialog
   */
  protected setActiveDialogNodeElements = (domNodeBody: HTMLElement = null) => {
    if (!domNodeBody) {
      domNodeBody = this.getActiveDialogNodeDom();
    }
    if (domNodeBody) {
      this.dialogNodeElements = getNodesFromContainerFromTypes(domNodeBody, ['button', 'a', 'input'], [{
        name: 'tabindex'
      }]);
      this.dialogNodeElements = this.dialogNodeElements.filter((item: any) => item.getAttribute('disabled') === null);
    } else {
      this.dialogNodeElements = [];
    }
  };

  /**
   * Sets focus to active dialog after showing
   */
  setFocusToActiveDialog = (typeFocus: string = 'opened') => {
    if (['opened', 'updateDialog'].includes(typeFocus) || !DispatcherWindowManager.previousFocusToDo) {
      if (typeFocus === 'opened') {
        let fakeCloseIcon: any;
        if (this.fakeCloseIconRef) {
          fakeCloseIcon = this.getDomNode(this.fakeCloseIconRef.current);
          if (fakeCloseIcon) {
            fakeCloseIcon.focus();
          }
          setTimeout(() => {
            this.repeatFakeIconFocus();
          }, 1);
        }
      } else if (typeFocus === 'updateDialog') {
        const lastFocusedElement = this.lastFocusedElement.getDomElement();
        if (lastFocusedElement) {
          lastFocusedElement.focus();
        } else if (this.lastFocusedElement?.origin?.focus) {
          this.lastFocusedElement.origin.focus();
        }
        setTimeout(() => {
          this.repeatLastFocus();
        }, 1);
      }
    }
  };

  private repeatFakeIconFocus = () => {
    const fakeCloseIcon = this.getDomNode(this.fakeCloseIconRef.current);
    if (fakeCloseIcon && document.activeElement !== fakeCloseIcon) {
      fakeCloseIcon.focus();
    }
    if (!fakeCloseIcon) {
      const domNodeBody = this.getActiveDialogNodeDom();
      if (domNodeBody && this.enableToSetFocusToFirstField) {
        setTimeout(() => {
          this.setFocusToFirstInputFieldOrButton(domNodeBody);
        }, 1);
      }
    }
  };

  private repeatLastFocus = () => {
    const lastFocusedElement = this.lastFocusedElement.getDomElement();
    if (lastFocusedElement && document.activeElement !== lastFocusedElement) {
      lastFocusedElement.focus();
    }
  };

  private setMultiWindowState = (windows: any): void => {
    this.setState({
      windows: windows
    });
  };

  componentDidMount(): void {
    super.componentDidMount();
    if (!this.multiWindow) {
      this.hide();
    }
  }

  componentDidUpdate() {
    const el = document.querySelector('div.k-widget.k-window.k-dialog');
    if (el) {
      el.removeAttribute('aria-labelledby');
      // el.removeAttribute('role');
    }
    let dispatcher = Dispatcher.appDispatchers[this.dispatcherId];
    if (dispatcher) {
      const { appCurrentOperation } = dispatcher;
      if (appCurrentOperation) {
        this.setActiveDialogNodeElements();
        const isShowOrHide = ['show', 'hide'].includes(appCurrentOperation.name);
        const isModuleNameWarningOrDialog = ['WarningDialog', 'Dialog'].includes(appCurrentOperation.moduleName);
        const isSynchronous = appCurrentOperation.synchronous;
        const isUpdateDialog = appCurrentOperation.type === OperationType.updateDialog;
        if (isSynchronous && isShowOrHide) {
          if (isModuleNameWarningOrDialog) {
            if (this.multiWindow) {
              if (appCurrentOperation.name === 'show') {
                this.setFocusToActiveDialog();
              } else if (appCurrentOperation.name === 'hide') {
                DispatcherWindowManager.setPreviousFocusToView();
              }
              Dispatcher.toDispatcher(false, this.dispatcherId);
            } else {
              let { layerCSS, dispatcherId } = this.state;
              // if (layerCSS !== 'tp-display-none') {
              Dispatcher.toDispatcher(false, dispatcherId);
              // }
            }
          }
        } else if (isUpdateDialog) {
          this.setFocusToActiveDialog('updateDialog');
          Dispatcher.toDispatcher(false, this.dispatcherId);
        }
      }
    }
  }

  renderMessage(windowObj?) {
    if (!this.multiWindow) {
      windowObj = this.state;
    }
    let { text = null, subTitle = null } = windowObj as any;
    return (
      <div
        className={'tp-warning-text'}
        role={'alert'}
      >
        <span>
          {
            subTitle ?
              <div
                className={'tp-warning-subtitle tp-text-semibold tp-font-size-md'}
              >{subTitle}</div> :
              null
          }
          {
            text ?
              <div
                className={'tp-warning-text-body'}
              >{text}</div> :
              null
          }
        </span>
      </div>
    );
  }

  /**
   * Renders footer for dialogs.
   */
  renderToolbar(type: DockType = DockType.bottom, windowObj?, index?) {
    if (!this.multiWindow) {
      windowObj = this.state;
      this.escapeActionName = '';
    } else if (_.isNumber(index)) {
      _.assign(this.multiWindowParams[index], {
        escapeActionName: '',
        escapeActionStopped: false
      });
    }
    let buttonStateDisabledName;
    let buttonStateDisabled;
    let buttonStateHiddenName;
    let buttonStateHidden;
    let customTagArgsName;
    let customTagArgs;
    let customTagHiddenName;
    let customTagHidden;
    let customTagStateDisabledName;
    let customTagStateDisabled;
    let hidden;

    let refTag = {};
    if (this.fakeCloseButton && windowObj.closable) {
      windowObj.fakeClosable = true;
      windowObj.closable = false;
      const dialogType = this.translate('dialogType');
      const dialogTitle = windowObj.title;
      const dialogClose = this.translate('close');
      const ariaLabelTitle = `${dialogType} ${dialogTitle} ${dialogClose}`;
      windowObj.buttons.push({
        name: 'fakeCloseIcon',
        btnConfig: {
          args: [index, this.closeDialog, ariaLabelTitle],
          customTagType: CustomTagType.closeIcon,
          dock: DockType.top,
          pos: Position.endCorner
        }
      });
    }

    const isFakeCloseIconRefEnabled = this.fakeCloseButton && (windowObj.closable || windowObj.fakeClosable);

    const items = (windowObj.buttons || []).reduce((arr: any, item: DialogButton) => {
      const { btnConfig = {}, actionName = '', name = '', close = true } = item;
      let { dock = DockType.bottom, accessibility = {} } = btnConfig;
      if (name === 'ESCAPE') {
        if (this.multiWindow) {
          if (_.isNumber(index)) {
            this.multiWindowParams[index].escapeActionName = actionName;
            _.assign(this.multiWindowParams[index], {
              escapeActionName: actionName,
              escapeActionStopped: !close
            });
          }
        } else {
          this.escapeActionName = actionName;
        }
      } else if (dock === type) {
        if (!btnConfig.customTagType) {
          buttonStateHiddenName = `hidden_${name.replace(/\./g, '_')}`;
          buttonStateHidden = _.get(windowObj, buttonStateHiddenName);
          hidden = _.isUndefined(buttonStateHidden) ? btnConfig.hidden : buttonStateHidden;
          if (this.multiWindow) {
            if (_.isNumber(index)) {
              _.set(this.multiWindowParams[index].defaultFooterState, buttonStateHiddenName, undefined);
            }
          } else {
            _.set(this.defaultFooterState, buttonStateHiddenName, undefined);
          }
          const isCancelButton = !!name.match(/cancel/);
          if (isCancelButton) {
            if (this.multiWindow) {
              if (_.isNumber(index)) {
                this.multiWindowParams[index].closeAction = actionName;
              }
            } else {
              this.closeAction = actionName;
            }
          }
          if (!hidden) {
            buttonStateDisabledName = `disabled_${name.replace(/\./g, '_')}`;
            buttonStateDisabled = _.get(windowObj, buttonStateDisabledName);
            if (this.multiWindow) {
              if (_.isNumber(index)) {
                _.set(this.multiWindowParams[index].defaultFooterState, buttonStateDisabledName, undefined);
              }
            } else {
              _.set(this.defaultFooterState, buttonStateDisabledName, undefined);
            }

            let accessibilityContent = {};
            if (accessibility === true) {
              accessibility = {};
            }
            if (_.isObject(accessibility) && accessibility.ariaLabel) {
              accessibilityContent['aria-label'] = this.translate(accessibility.ariaLabel, windowObj.placeholders);
            }

            let config = {
              autoFocus: isCancelButton, // autofocus cancel buttons on dialogs
              className: btnConfig.className || '',
              // <debug>
              'data-cy': `${this.moduleName}_button_${name}`,
              // </debug>
              disabled: _.isUndefined(buttonStateDisabled) ? btnConfig.disabled : buttonStateDisabled,
              key: actionName + name,
              fillMode: btnConfig.fillMode || 'solid',
              onClick: () => this.dispatch(actionName, {
                dispatcherId: this.dispatcherId,
                pos: Position.end
              }),
              themeColor: btnConfig.themeColor || 'base',
              ...accessibilityContent
            };

            if (btnConfig.iconClass) {
              _.set(config, 'iconClass', btnConfig.iconClass);
            }
            const button = this.getTypeOfButton(config, name, windowObj, !!accessibility);
            if (btnConfig.pos === Position.endCorner) {
              arr[2].push(button);
            } else {
              arr[btnConfig.pos === Position.end ? 1 : 0].push(button);
            }
          }
        } else {
          const { args, customTagType, disabled } = btnConfig;

          customTagHiddenName = `customTagHidden_${name.replace(/\./g, '_')}`;
          customTagHidden = _.get(windowObj, customTagHiddenName);
          hidden = _.isUndefined(customTagHidden) ? btnConfig.hidden : customTagHidden;
          if (this.multiWindow) {
            if (_.isNumber(index)) {
              _.set(this.multiWindowParams[index].defaultFooterState, customTagHiddenName, undefined);
            }
          } else {
            _.set(this.defaultFooterState, customTagHiddenName, undefined);
          }

          if (!hidden) {
            customTagStateDisabledName = `customTagDisabled_${name.replace(/\./g, '_')}`;
            customTagStateDisabled = _.get(windowObj, customTagStateDisabledName);
            if (this.multiWindow) {
              if (_.isNumber(index)) {
                _.set(this.multiWindowParams[index].defaultFooterState, customTagStateDisabledName, undefined);
              }
            } else {
              _.set(this.defaultFooterState, customTagStateDisabledName, undefined);
            }

            customTagArgsName = `customTagArgs_${name.replace(/\./g, '_')}`;
            if (this.multiWindow) {
              if (_.isNumber(index)) {
                _.set(this.multiWindowParams[index].defaultFooterState, customTagArgsName, undefined);
              }
            } else {
              _.set(this.defaultFooterState, customTagArgsName, undefined);
            }
            customTagArgs = _.get(windowObj, customTagArgsName);

            if (isFakeCloseIconRefEnabled && customTagType === CustomTagType.closeIcon) {
              refTag = {
                ref: this.fakeCloseIconRef
              };
            }
            const customTag = (
              <CustomTag
                {...refTag}
                args={_.isUndefined(customTagArgs) ? args : customTagArgs}
                disabled={_.isUndefined(customTagStateDisabled) ? btnConfig.disabled : customTagStateDisabled}
                name={name}
                type={customTagType}
                key={uuid()}
              />);
            if (btnConfig.pos === Position.endCorner) {
              arr[2].push(customTag);
            } else {
              arr[btnConfig.pos === Position.end ? 1 : 0].push(customTag);
            }
          }
        }
      }
      return arr;
    },
    [[], [], []] // 0: left (pos:start), 1: right (pos:end), 2: extremely right (pos:endCorner)
    );

    const [left, right, extremelyRight] = items;
    let toolbar: any = '';
    const dialogFooterToolbarCls = classNames(
      'tp-dialog-footer-toolbar',
      {
        'tp-left-and-right': left.length && right.length,
        'tp-left': left.length,
        'tp-right': right.length
      }
    );
    if (type === DockType.top) {
      toolbar = this.getTopToolbar(left, right, extremelyRight, windowObj);
    } else {
      toolbar =
        left.length || right.length ? (
          <DialogActionsBar
            children={[<div
              key="tp-dialog-footer-toolbar"
              className={dialogFooterToolbarCls}
            >
              {left}
              <div className="k-flex-1" />
              {right}
            </div>]}
          />
        ) : null;
    }
    return toolbar;
  }

  /**
   * Returns Resizer Component that is used for draggable resizing
   * @param {Boolean} resizable
   */
  getResizer = (resizable: boolean): Resizer | any => (
    resizable ? (
      <Resizer
        onResize={this.handleResize}
      />) : null
  )

  /**
   * Sets `dragContainer` class property
   * @param event
   */
  private getDragContainer = (event: any) => {
    this.dragContainer = event.originalEvent.target?.offsetParent;
    if (!this.dragContainer) {
      return;
    }
    const querySelector = new QuerySelector(event.originalEvent.target.offsetParent);
    let querySelectorOriginalPath = querySelector.pathSelector;
    const pathSelector = [];
    querySelectorOriginalPath = querySelectorOriginalPath[0];
    for (let i = 0; i < querySelectorOriginalPath.length; i++) {
      pathSelector.push(querySelectorOriginalPath[i]);
      if (querySelectorOriginalPath[i].search('div.k-window.k-dialog') !== -1) {
        break;
      }
    }
    querySelector.pathSelector = [pathSelector];
    this.dragContainer = querySelector.getDomElement();
  }

  /**
   * Listens drag resize event
   * @param event
   * @param {HANDLE_RESIZE_PROPS} props
   */
  private handleResize = (event: any, props: HANDLE_RESIZE_PROPS) => {
    const { end, pos } = props;
    if (!end) {
      if (this.dragXPos === -1 && this.dragYPos === -1) {
        this.getDragContainer(event);
        if (!this.dragContainer) {
          return;
        }
      }
      if (this.dragXPos === -1) {
        this.dragXPos = event.clientX;
      }
      if (this.dragYPos === -1) {
        this.dragYPos = event.clientY;
      }
      let newWidth;
      let newHeight;
      switch (pos) {
        case POSITION.west:
          newWidth = `${this.dragContainer.offsetWidth + 2 * (this.dragXPos - event.clientX)}px`;
          break;
        case POSITION.east:
          newWidth = `${this.dragContainer.offsetWidth + 2 * (event.clientX - this.dragXPos)}px`;
          break;
        case POSITION.south:
          newHeight = `${this.dragContainer.offsetHeight + 2 * (event.clientY - this.dragYPos)}px`;
          break;
        case POSITION.north:
          newHeight = `${this.dragContainer.offsetHeight + 2 * (this.dragYPos - event.clientY)}px`;
          break;
        case POSITION.southWest:
          newWidth = `${this.dragContainer.offsetWidth + 2 * (this.dragXPos - event.clientX)}px`;
          newHeight = `${this.dragContainer.offsetHeight + 2 * (event.clientY - this.dragYPos)}px`;
          break;
        case POSITION.northWest:
          newWidth = `${this.dragContainer.offsetWidth + 2 * (this.dragXPos - event.clientX)}px`;
          newHeight = `${this.dragContainer.offsetHeight + 2 * (this.dragYPos - event.clientY)}px`;
          break;
        case POSITION.southEast:
          newWidth = `${this.dragContainer.offsetWidth + 2 * (event.clientX - this.dragXPos)}px`;
          newHeight = `${this.dragContainer.offsetHeight + 2 * (event.clientY - this.dragYPos)}px`;
          break;
        case POSITION.northEast:
          newWidth = `${this.dragContainer.offsetWidth + 2 * (event.clientX - this.dragXPos)}px`;
          newHeight = `${this.dragContainer.offsetHeight + 2 * (this.dragYPos - event.clientY)}px`;
          break;
        default:
          break;
      }
      this.dragContainer.style.width = newWidth;
      this.dragContainer.style.maxWidth = newWidth;
      this.dragContainer.style.height = newHeight;
      this.dragContainer.style.maxHeight = newHeight;
      this.dragXPos = event.clientX;
      this.dragYPos = event.clientY;
    } else {
      this.dragXPos = -1;
      this.dragYPos = -1;
    }
  }

  /**
   * Returns CustomButton or Button in case if button has accessibility prop
   * @param config
   * @param name
   * @param windowObj
   * @param isCustom
   */
  getTypeOfButton = (config: any, name: string, windowObj: any, isCustom: boolean) => {
    const ButtonType: any = isCustom ? CustomButton : Button;
    return (
      <ButtonType
        key={name}
        {...config}
      >
        {this.translate(name, windowObj.placeholders)}
      </ButtonType>
    );
  };

  /**
   * Updates configuration of footer buttons and custom tags.
   * @param newState
   * @param {UpdateDialogScope} scope What dialog should be updated
   */
  updateFooterItems(newState: any, scope: UpdateDialogScope) {
    if (this.multiWindow) {
      let windows = this.state.windows.slice();
      if (windows.length) {
        if (scope === UpdateDialogScope.active) {
          windows[windows.length - 1] = {
            ...windows[windows.length - 1],
            ...this.multiWindowParams[windows.length - 1].defaultFooterState,
            ...newState
          };
        } else {
          windows = windows.map((item, index) => ({
            ...item,
            ...this.multiWindowParams[index].defaultFooterState,
            ...newState
          }));
        }
        this.setState({
          windows: windows
        });
      } else {
        Dispatcher.toDispatcher(false, this.dispatcherId);
      }
    } else {
      this.setState(newState);
    }
  }

  /**
   * Gets close action name based on moduleName.
   */
  getCloseActionName(): string {
    return `close${this.moduleName}`;
  }

  /**
   * Returns additional classes prefixed with WarningDialog.baseClassPrefix that will be applied to dialog.
   */
  getDialogAdditionalClasses(item?) {
    if (!this.multiWindow) {
      item = this.state;
    }
    let classes = (item.className || this.defaultSize).replace(/\s+/g, ' ').split(' ');
    return `${this.baseClassPrefix}${classes.join(' ' + this.baseClassPrefix)}`;
  }

  closeDialog = (e: DialogCloseEvent, index?) => {
    e.nativeEvent.preventDefault();

    // TODO Kendo React ticket = 1525496, This block of code is temporarily solution
    let animationContainers: any = document.querySelectorAll(`.${ANIMATION_CONTAINER_CLASS}`);
    let animationContainer = null;
    let animationContainerZIndex = '-1';
    let isDialogPopupVisible = false;
    if (animationContainers.length > 0) {
      const animationContainerFilters = [];
      animationContainers.forEach((item) => {
        if (item.parentElement.nodeName.toLowerCase() === 'body') {
          animationContainerFilters.push(item);
        }
      });
      if (animationContainerFilters.length === 1) {
        animationContainer = animationContainerFilters[0];
        animationContainerZIndex = getComputedStyle(animationContainer).getPropertyValue('z-index');
        isDialogPopupVisible = this.queryId === '#dialog-modal-root' && animationContainer && animationContainerZIndex === '12000';
      }
    }
    if (!isDialogPopupVisible) {
      // TODO End of block

      const closeAction = this.getCloseActionName();
      const actionType: ActionType = {
        dispatcherId: this.dispatcherId,
        pos: Position.end
      };
      if (this.multiWindow) {
        const escapeActionStopped = _.get(this.multiWindowParams[index], 'escapeActionStopped');
        const escapeActionName = _.get(this.multiWindowParams[index], 'escapeActionName');
        if (escapeActionName) {
          this.dispatch(escapeActionName, actionType);
        }
        if (!escapeActionStopped) {
          this.dispatch(closeAction, actionType);
        }
      } else {
        if (this.escapeActionName) {
          this.dispatch(this.escapeActionName, actionType);
        }
      }
    }
  };

  getChildren = (windowObj?, index?) => [this.renderMessage(windowObj), this.renderToolbar(DockType.bottom, windowObj, index)] as any[];

  getDialog(item: any = {}, index?) {
    if (!this.multiWindow) {
      item = this.state;
    }
    const { closable = true, accessibility, resizable } = item;
    const closeIcon = this.fakeCloseButton ? false : closable;
    const zIndexClsPrefix = DispatcherWindowManager.zIndexClsPrefix;
    const zIndexCls = this.moduleName === 'WarningDialog' ? `${zIndexClsPrefix}warning` : `${zIndexClsPrefix}dialog`;
    const refName: any = `${this.moduleName}_${index}`;

    const accessibilityContent = accessibility ? {
      ['aria-live']: accessibility.ariaLive,
      role: accessibility.role
    } : {};

    const dialogWrapperCls = classNames(
      'tp-modal-dialog-wrapper data-preventrecorddeselect',
      { 'tp-dialog-resize': resizable },
      zIndexCls,
      item.warningType && this.getDialogAdditionalClasses(this.warningClassName[item.warningType]),
      !item.warningType && this.getDialogAdditionalClasses(item),
      { 'tp-dialog-resize': resizable },
      { 'tp-dialog-with-custom-close-btn': this.fakeCloseButton }
    );
    return (
      <div
        {...accessibilityContent}
        onKeyDown={e => this.onKeyDown(e)}
      >
        <KendoDialog
          autoFocus={true}
          ref={refName}
          title={this.renderToolbar(DockType.top, item, index)}
          onClose={e => this.closeDialog(e, index)}
          closeIcon={closeIcon}
          className={dialogWrapperCls}
          children={this.childrenWithKeys(item, index)}
        />
      </div>
    );
  }

  private onKeyDown = (e: any) => {
    if (e.keyCode === Keys.tab) {
      const len = this.state.windows.length;
      if (len) {
        const focusNode = document.activeElement;
        const index = this.dialogNodeElements.findIndex(element => element === focusNode);
        if (index !== -1) {
          if (e.shiftKey) {
            if (this.dialogNodeElements[index].getAttribute('tabIndex') == -1) {
              e.preventDefault();
              this.moveToFocusableNode(index, true);
            }
          } else {
            if (!this.dialogNodeElements[index + 1] || this.dialogNodeElements[index + 1].getAttribute('tabIndex') == -1) {
              e.preventDefault();
              this.moveToFocusableNode(index);
            }
          }
        }
      }
    } else if (e.keyCode === Keys.right || e.keyCode === Keys.left) {
      this.setActiveDialogNodeElements();
    }
  };

  /**
   * If you type tab this method will trow focus to first focusable node that is located in dialog
   * @param {Number} index Index of focused node
   * @param {Boolean} shiftKey
   */
  private moveToFocusableNode = (index: number, shiftKey: boolean = false) => {
    const len = this.dialogNodeElements.length;
    let focusableNode = null;
    let counter = 0;
    let increment;
    let focusableNodeIndex;
    if (shiftKey) {
      focusableNodeIndex = index - 1;
      if (focusableNodeIndex < 0) {
        focusableNodeIndex = len - 1;
      }
      increment = -1;
    } else {
      focusableNodeIndex = index + 1;
      if (focusableNodeIndex > len - 1) {
        focusableNodeIndex = 0;
      }
      increment = 1;
    }
    while (!focusableNode && counter < len) {
      focusableNode = this.dialogNodeElements[focusableNodeIndex];
      if (focusableNode && focusableNode.getAttribute('tabIndex') == -1) {
        focusableNode = null;
      }
      counter++;
      focusableNodeIndex += increment;
      if (focusableNodeIndex < 0) {
        focusableNodeIndex = len - 1;
      } else if (focusableNodeIndex > len - 1) {
        focusableNodeIndex = 0;
      }
    }
    if (focusableNode) {
      focusableNode.focus();
    }
  };

  /**
   * Renders toolbar to header when DockType.top is passed as dock in buttonConfig.
   * @param left
   * @param right
   * @param extremelyRight
   * @param windowObj
   */
  private getTopToolbar(left: JSX.Element[], right: JSX.Element[], extremelyRight: JSX.Element[], windowObj?): JSX.Element {
    if (!this.multiWindow) {
      windowObj = this.state;
    }

    const { title, id } = windowObj;

    const key = _.isString(title) ? title.split(' ').join('-') : id || uuid();
    const attributes = {
      // <debug>
      'data-cy': `cy-${this.moduleName}-title`,
      // </debug>
      'className': 'tp-dialog-title k-text-ellipsis',
      key
    };
    const customTitleBarCls = classNames(
      'tp-dialog-custom-titlebar k-hbox k-flex', {
        'tp-with-right-element': right.length > 0
      }
    );
    left = [<h2 {...attributes}>{windowObj.title}</h2>, ...left];

    const FakeTitleContentEl = (props: any) => (
      <div
        className={customTitleBarCls}
      >
        {props.children}
      </div>
    );
    const TitleContentEl = (props: any) => (<>
      {props.children}
    </>);
    const TitleContent: any = this.fakeCloseButton ? FakeTitleContentEl : TitleContentEl;

    return (
      <>
        <TitleContent>
          {extremelyRight.length > 0 && <div
            className={'tp-dialog-header-extremely-right-items'}
          >
            {extremelyRight}
          </div>}
          {left.length > 0 && <div className={'tp-dialog-header-left-items'}>
            {left}
          </div>
          }
          {right.length > 0 && <div
            className={'tp-dialog-header-right-items k-text-ellipsis'}
          >
            {right}
          </div>}
        </TitleContent>
        {this.getResizer(windowObj?.resizable)}
      </>
    );
  }

  /**
   * Returns children elements with changed keys to preserve the semantic of nested arrays when flattening lists of
   * children.
   */
  childrenWithKeys = (windowObj?, index?) => {
    let children = this.getChildren(windowObj, index).filter((item: any) => item);
    return React.Children.toArray(children);
  };

  renderBody(): JSX.Element | JSX.Element[] | ReactPortal | ReactPortal[] | null | null[] {
    const activeEl = document.activeElement;
    if (activeEl.nodeName.toLowerCase() !== 'body') {
      this.lastFocusedElement = new QuerySelector(activeEl);
    }

    let renders;
    if (this.multiWindow) {
      renders = this.state.windows.map((item, index) => item.layerCSS !== 'tp-display-none' ? ReactDOM.createPortal(this.getDialog(item, index), document.querySelector(this.queryId) as any) : null);
    } else {
      renders = this.state.layerCSS !== 'tp-display-none' ? ReactDOM.createPortal(this.getDialog(), document.querySelector(this.queryId) as any) : null;
    }
    return renders;
  }
}

export default WarningDialog;
